import {
  ActionButton,
  OfficeButton,
  OfficeHeader,
  Paginator,
} from "components";
import { FC, useMemo } from "react";
import {
  useAppLocation,
  useAppNavigate,
  useAppSearch,
} from "_graphql/cache/auth";

import { Action } from "components/buttons/action-button";
import CreateTargetBadgeContainer from "./create";
import { FetchTargetBadge_fetchTargetBadge } from "_graphql/queries/__generated__/fetchTargetBadge";
import RemoveTargetBadgeContainer from "./remove";
import Table from "components/tables/table";
import { TagIcon } from "@heroicons/react/24/outline";
import UpdateTargetBadgeContainer from "./update";
import ViewTargetBadgeContainer from "./view";
import { usePagination } from "hooks";
import { useTarget } from "hooks/data/use-targets";

type TargetBadgesPageProps = {};

const TargetBadgesPage: FC<TargetBadgesPageProps> = () => {
  const { params } = useAppLocation();
  const searchParams = useAppSearch();
  const navigate = useAppNavigate();
  const { offset, limit, setPage } = usePagination();

  const { badges, loading, refetch, targetBadge } = useTarget({
    targetId: params?.id || "",
  });

  // Calculate length for pagination
  const length = useMemo(() => badges?.length || 0, [badges]);

  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
      () => {
        navigate({
          search: (old) => ({
            ...old,
            id,
            modal: action,
          }),
        });
      };

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <OfficeButton
            onClick={() =>
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    modal: "create",
                  };
                },
              })
            }
          >
            Configure Badge
          </OfficeButton>
        )}
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <Table
            title={`Target: ${targetBadge?.target_name || params?.id || ""}`}
            loading={loading}
            hasSearch
            columns={[
              {
                name: "Badge Name",
                accessor: "name",
                render: (item: FetchTargetBadge_fetchTargetBadge) => (
                  <div className="flex items-center space-x-2">
                    <TagIcon className="h-6 w-6" />
                    <div className="flex-1 flex flex-col">
                      <span>{item.name}</span>
                      <span className="font-light text-xs">
                        {item.description}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Points Range",
                accessor: "points",
                render: (item: FetchTargetBadge_fetchTargetBadge) => (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 flex flex-col">
                      <span>
                        {item.min_points || 0} - {item.max_points || 0}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Cash Equivalent",
                accessor: "cash_equivalent",
                render: (item: FetchTargetBadge_fetchTargetBadge) => (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 flex flex-col">
                      <span>
                        Ghs {item.minimum_cash_equivalent || 0} - Ghs{" "}
                        {item.cash_equivalent || 0}
                      </span>
                    </div>
                  </div>
                ),
              },
              {
                name: "Actions",
                accessor: "id",
                render: (item: FetchTargetBadge_fetchTargetBadge) => (
                  <>
                    <ActionButton
                      action="view"
                      onClick={dispatchAction(item?.id || "", "view")}
                    />
                    <ActionButton
                      action="update"
                      onClick={dispatchAction(item?.id || "", "update")}
                    />
                    <ActionButton
                      action="remove"
                      extraClassName="text-red-400"
                      onClick={dispatchAction(item?.id || "", "remove")}
                    />
                  </>
                ),
              },
            ]}
            data={badges}
            refetch={refetch}
            pagination={() => (
              <Paginator
                offset={offset}
                limit={limit}
                totalItems={length}
                currentSize={badges.length}
                setOffset={setPage}
              />
            )}
          />
        </div>
      </div>

      <CreateTargetBadgeContainer
        open={searchParams?.modal === "create"}
        setOpen={(value) => {
          navigate({
            search(prev) {
              return {
                ...prev,
                modal: value ? "create" : undefined,
              };
            },
          });
        }}
        targetId={params?.id || ""}
        refetch={refetch}
      />

      {searchParams?.id && (
        <>
          <ViewTargetBadgeContainer
            open={searchParams?.modal === "view"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "view" : undefined,
                  };
                },
              });
            }}
          />

          <UpdateTargetBadgeContainer
            open={searchParams?.modal === "update"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "update" : undefined,
                  };
                },
              });
            }}
          />

          <RemoveTargetBadgeContainer
            open={searchParams?.modal === "remove"}
            setOpen={(value) => {
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    id: value ? searchParams?.id : undefined,
                    modal: value ? "remove" : undefined,
                  };
                },
              });
            }}
          />
        </>
      )}
    </main>
  );
};

export default TargetBadgesPage;
